import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useFormik } from "formik";
import { t } from "i18next";
import { AnimatePresence, motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";

import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { ToggleTextWhitLabel } from "../atoms/ToggleTextWhitLabel";
import { TextAreaHint } from "../atoms/TextAreaHint";
import { ButtonSoft } from "../atoms/ButtonSoft";
import { Select } from "./Select";

import { useGetAdvanceQuery } from "@/app/users/users.api";
import { InputFilepdf } from "../atoms/InputFilePDF";
import {
  usePostHealthMutation,
  useStateHealthQuery,
} from "@/app/users/StateHealth.api";
import { DialogCreate } from "../molecules/DialogCreate";
import RenderFormUploadList from "./RenderFormUploadList";
import {
  useDeleteMedicalReportMutation,
  useMedicalReportQuery,
} from "@/app/progreso/progress.api";

const optionsAlcohol = [
  { id: "Si", name: t("general.yes") },
  { id: "No", name: t("general.no") },
  { id: "Esporadico", name: t("user.views.select_combobox_sporadic") },
];

const FormValuationStateHealth = ({ user_id }: any) => {
  const { data: stateHealthData, isLoading: isLoadingStateHealth } =
    useStateHealthQuery({ id: user_id });

  const { data: medicalReports, refetch: refetchMedicalReports  , isLoading: isLoadingMedicalReports } =
    useMedicalReportQuery({ id: user_id });

  const [deleteMedicalReport] = useDeleteMedicalReportMutation();

  console.log("Data:===", stateHealthData);

  const formik = useFormik({
    initialValues: {
      fumador: false,
      optionsAlcohol: [],
      selectedAlcohol: "",
      dormirBien: false,
      tomarMedicamentos: false,
      problemaMedico: false,
      analiticaAlterada: false,
      presionAlterada: false,
      affectPeriod: false,
      amenorrea: false,
      irregulaPeriod: false,
      riskStatus: false,
      seriousInjuries: false,
      ankle: false,
      knee: false,
      hip: false,
      elbow: false,
      lumbar: false,
      cervical: false,
      deltoid: false,
      hand: false,
      activtyFisical: false,
      equipeGym: false,
      intolerance: false,
      supplementation: false,
      useSupplementation: false,
    },
    onSubmit: async (values) => {
      console.log("submit");
      try {
        console.log("Form values:", values);
        setShowUploadModal(true);
      } catch (error) {
        console.error("Error submitting form:", error);
      }
    },
  });

  const { id } = useParams<{ id: string }>();
  const { data } = useGetAdvanceQuery({ id });

  const [postHealth, { isLoading }] = usePostHealthMutation();

  const [useSupplementation, setUseSupplementation] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<
    { name: string; file: File }[]
  >([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    console.log("Valores del formik:", formik.values);
  }, [formik.values]);

  const [init, setInit] = useState(false);

  useEffect(() => {
    if (stateHealthData) {
      formik.setValues(stateHealthData?.data);
      setInit(true);
    }
  }, [stateHealthData, isLoadingStateHealth, id]);

  useEffect(() => {
    if (init) {
      const payload = {
        user_id: id,
        ...formik.values,
      };

      console.log("Payload:", payload);
      postHealth({
        body: payload,
      });
    }
  }, [formik.values]);

  return (
    <>
      <form
        onSubmit={formik.handleSubmit}
        className="max-w-full grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4"
      >
        <div>
          {/* Fumador */}
          <div className="flex items-center justify-between mt-5 gap-x-4">
            <label className="block text-sm font-medium text-gray-900">
              {t("user.views.smoker")}
            </label>
            <div className="w-40">
              <ToggleTextWhitLabel
                checked={formik.values.fumador}
                onChange={() =>
                  formik.setFieldValue("fumador", !formik.values.fumador)
                }
                texto={
                  formik.values.fumador ? t("general.yes") : t("general.no")
                }
              />
            </div>
          </div>

          {/* Alcohol */}
          <div className="flex items-center justify-between mt-5 gap-x-4">
            <label className="block text-sm font-medium text-gray-900">
              {t("user.views.alcohol_frequently")}
            </label>
            <div className="w-40">
              <Select
                name="selectedAlcohol"
                tittle=""
                selected={optionsAlcohol.find(
                  (option) => option.id === formik.values.selectedAlcohol
                )}
                setSelected={(option: any) =>
                  formik.setFieldValue("selectedAlcohol", option.id)
                }
                options={optionsAlcohol}
              />
            </div>
          </div>

          {/* Horas de sueño */}
          <div className="flex items-center justify-between mt-5 gap-x-4">
            <label className="block text-sm font-medium text-gray-900">
              {t("user.views.hours_sleep")}
            </label>
            <div className="w-20">
              <InputWithCornerHint
                label=""
                type="number"
                name="hours_sleep"
                value={formik.values.hours_sleep}
                onChange={formik.handleChange}
              />
            </div>
          </div>

          {/* Dormir bien */}
          <div className="flex items-center justify-between mt-5 gap-x-4">
            <label className="block text-sm font-medium text-gray-900">
              {t("user.views.sleep_well")}
            </label>
            <div className="w-40">
              <ToggleTextWhitLabel
                checked={formik.values.dormirBien}
                onChange={() =>
                  formik.setFieldValue("dormirBien", !formik.values.dormirBien)
                }
                texto={
                  formik.values.dormirBien ? t("general.yes") : t("general.no")
                }
              />
            </div>
          </div>

          {/* Medicamentos */}
          <div className="mt-5">
            <div className="flex items-center justify-between gap-x-4">
              <label className="block text-sm font-medium text-gray-900">
                {t("user.views.medication")}
              </label>
              <div className="w-40">
                <ToggleTextWhitLabel
                  checked={formik.values.tomarMedicamentos}
                  onChange={() =>
                    formik.setFieldValue(
                      "tomarMedicamentos",
                      !formik.values.tomarMedicamentos
                    )
                  }
                  texto={
                    formik.values.tomarMedicamentos
                      ? t("general.yes")
                      : t("general.no")
                  }
                />
              </div>
            </div>
            {formik.values.tomarMedicamentos && (
              <div className="mt-4">
                <TextAreaHint
                  label=""
                  name="medication_description"
                  value={formik.values.medication_description}
                  onChange={formik.handleChange}
                />
              </div>
            )}
          </div>

          {/* Problema médico */}
          <div className="mt-5">
            <div className="flex items-center justify-between gap-x-4">
              <label className="block text-sm font-medium text-gray-900">
                {t("user.views.medical_problem")}
              </label>
              <div className="w-40">
                <ToggleTextWhitLabel
                  checked={formik.values.problemaMedico}
                  onChange={() =>
                    formik.setFieldValue(
                      "problemaMedico",
                      !formik.values.problemaMedico
                    )
                  }
                  texto={
                    formik.values.problemaMedico
                      ? t("general.yes")
                      : t("general.no")
                  }
                />
              </div>
            </div>
            {formik.values.problemaMedico && (
              <div className="mt-4">
                <TextAreaHint
                  label=""
                  name="medical_problem_description"
                  value={formik.values.medical_problem_description}
                  onChange={formik.handleChange}
                />
              </div>
            )}
          </div>

          {/* Analítica alterada */}
          <div className="mt-5">
            <div className="flex items-center justify-between gap-x-4">
              <label className="block text-sm font-medium text-gray-900">
                {t("user.views.last_analytic_altered")}
              </label>
              <div className="w-40">
                <ToggleTextWhitLabel
                  checked={formik.values.analiticaAlterada}
                  onChange={() =>
                    formik.setFieldValue(
                      "analiticaAlterada",
                      !formik.values.analiticaAlterada
                    )
                  }
                  texto={
                    formik.values.analiticaAlterada
                      ? t("general.yes")
                      : t("general.no")
                  }
                />
              </div>
            </div>
            {formik.values.analiticaAlterada && (
              <div className="mt-4">
                <TextAreaHint
                  label=""
                  name="last_analytic_altered_description"
                  value={formik.values.last_analytic_altered_description}
                  onChange={formik.handleChange}
                />
              </div>
            )}
          </div>

          {/* Presión alterada */}
          <div className="mt-5">
            <div className="flex items-center justify-between gap-x-4">
              <label className="block text-sm font-medium text-gray-900">
                {t("user.views.altered_tension")}
              </label>
              <div className="w-40">
                <ToggleTextWhitLabel
                  checked={formik.values.presionAlterada}
                  onChange={() =>
                    formik.setFieldValue(
                      "presionAlterada",
                      !formik.values.presionAlterada
                    )
                  }
                  texto={
                    formik.values.presionAlterada
                      ? t("general.yes")
                      : t("general.no")
                  }
                />
              </div>
            </div>
            {formik.values.presionAlterada && (
              <div className="mt-4">
                <TextAreaHint
                  label=""
                  name="altered_tension_description"
                  value={formik.values.altered_tension_description}
                  onChange={formik.handleChange}
                />
              </div>
            )}
          </div>

          {/* Afecta período */}
          <div className="flex items-center justify-between mt-5 gap-x-4">
            <label className="block text-sm font-medium text-gray-900">
              {t("user.views.affects_period")}
            </label>
            <div className="w-40">
              <ToggleTextWhitLabel
                checked={formik.values.affectPeriod}
                onChange={() =>
                  formik.setFieldValue(
                    "affectPeriod",
                    !formik.values.affectPeriod
                  )
                }
                texto={
                  formik.values.affectPeriod
                    ? t("general.yes")
                    : t("general.no")
                }
              />
            </div>
          </div>

          {/* Amenorrea */}
          <div className="flex items-center justify-between mt-5 gap-x-4">
            <label className="block text-sm font-medium text-gray-900">
              {t("user.views.amenorrea")}
            </label>
            <div className="w-40">
              <ToggleTextWhitLabel
                checked={formik.values.amenorrea}
                onChange={() =>
                  formik.setFieldValue("amenorrea", !formik.values.amenorrea)
                }
                texto={
                  formik.values.amenorrea ? t("general.yes") : t("general.no")
                }
              />
            </div>
          </div>

          {/* Período irregular */}
          <div className="mt-5">
            <div className="flex items-center justify-between gap-x-4">
              <label className="block text-sm font-medium text-gray-900">
                {t("user.views.irregular_period")}
              </label>
              <div className="w-40">
                <ToggleTextWhitLabel
                  checked={formik.values.irregulaPeriod}
                  onChange={() =>
                    formik.setFieldValue(
                      "irregulaPeriod",
                      !formik.values.irregulaPeriod
                    )
                  }
                  texto={
                    formik.values.irregulaPeriod
                      ? t("general.yes")
                      : t("general.no")
                  }
                />
              </div>
            </div>
            {formik.values.irregulaPeriod && (
              <div className="mt-4">
                <TextAreaHint
                  label=""
                  name="irregular_period_frequency"
                  value={formik.values.irregular_period_frequency}
                  onChange={formik.handleChange}
                />
              </div>
            )}
          </div>

          {/* Estado de riesgo */}
          <div className="flex items-center justify-between mt-5 gap-x-4">
            <label className="block text-sm font-medium text-gray-900">
              {t("user.views.risk_status")}
            </label>
            <div className="w-40">
              <ToggleTextWhitLabel
                checked={formik.values.riskStatus}
                onChange={() =>
                  formik.setFieldValue("riskStatus", !formik.values.riskStatus)
                }
                texto={
                  formik.values.riskStatus ? t("general.yes") : t("general.no")
                }
              />
            </div>
          </div>

          {/* Lesiones graves */}
          <div className="mt-5">
            <div className="flex items-center justify-between gap-x-4">
              <label className="block text-sm font-medium text-gray-900">
                {t("user.views.serious_injuries")}
              </label>
              <div className="w-40">
                <ToggleTextWhitLabel
                  checked={formik.values.seriousInjuries}
                  onChange={() =>
                    formik.setFieldValue(
                      "seriousInjuries",
                      !formik.values.seriousInjuries
                    )
                  }
                  texto={
                    formik.values.seriousInjuries
                      ? t("general.yes")
                      : t("general.no")
                  }
                />
              </div>
            </div>
            {formik.values.seriousInjuries && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between gap-x-4">
                    <label className="block text-sm font-medium text-gray-900">
                      {t("user.views.ankle")}
                    </label>
                    <div className="w-40">
                      <ToggleTextWhitLabel
                        checked={formik.values.ankle}
                        onChange={() =>
                          formik.setFieldValue("ankle", !formik.values.ankle)
                        }
                        texto={
                          formik.values.ankle
                            ? t("general.yes")
                            : t("general.no")
                        }
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-5 gap-x-4">
                    <label className="block text-sm font-medium text-gray-900">
                      {t("user.views.knee")}
                    </label>
                    <div className="w-40">
                      <ToggleTextWhitLabel
                        checked={formik.values.knee}
                        onChange={() =>
                          formik.setFieldValue("knee", !formik.values.knee)
                        }
                        texto={
                          formik.values.knee
                            ? t("general.yes")
                            : t("general.no")
                        }
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-5 gap-x-4">
                    <label className="block text-sm font-medium text-gray-900">
                      {t("user.views.hip")}
                    </label>
                    <div className="w-40">
                      <ToggleTextWhitLabel
                        checked={formik.values.hip}
                        onChange={() =>
                          formik.setFieldValue("hip", !formik.values.hip)
                        }
                        texto={
                          formik.values.hip ? t("general.yes") : t("general.no")
                        }
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-5 gap-x-4">
                    <label className="block text-sm font-medium text-gray-900">
                      {t("user.views.elbow")}
                    </label>
                    <div className="w-40">
                      <ToggleTextWhitLabel
                        checked={formik.values.elbow}
                        onChange={() =>
                          formik.setFieldValue("elbow", !formik.values.elbow)
                        }
                        texto={
                          formik.values.elbow
                            ? t("general.yes")
                            : t("general.no")
                        }
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between gap-x-4">
                    <label className="block text-sm font-medium text-gray-900">
                      {t("user.views.lumbar")}
                    </label>
                    <div className="w-40">
                      <ToggleTextWhitLabel
                        checked={formik.values.lumbar}
                        onChange={() =>
                          formik.setFieldValue("lumbar", !formik.values.lumbar)
                        }
                        texto={
                          formik.values.lumbar
                            ? t("general.yes")
                            : t("general.no")
                        }
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-5 gap-x-4">
                    <label className="block text-sm font-medium text-gray-900">
                      {t("user.views.cervical")}
                    </label>
                    <div className="w-40">
                      <ToggleTextWhitLabel
                        checked={formik.values.cervical}
                        onChange={() =>
                          formik.setFieldValue(
                            "cervical",
                            !formik.values.cervical
                          )
                        }
                        texto={
                          formik.values.cervical
                            ? t("general.yes")
                            : t("general.no")
                        }
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-5 gap-x-4">
                    <label className="block text-sm font-medium text-gray-900">
                      {t("user.views.deltoid")}
                    </label>
                    <div className="w-40">
                      <ToggleTextWhitLabel
                        checked={formik.values.deltoid}
                        onChange={() =>
                          formik.setFieldValue(
                            "deltoid",
                            !formik.values.deltoid
                          )
                        }
                        texto={
                          formik.values.deltoid
                            ? t("general.yes")
                            : t("general.no")
                        }
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-5 gap-x-4">
                    <label className="block text-sm font-medium text-gray-900">
                      {t("user.views.hand")}
                    </label>
                    <div className="w-40">
                      <ToggleTextWhitLabel
                        checked={formik.values.hand}
                        onChange={() =>
                          formik.setFieldValue("hand", !formik.values.hand)
                        }
                        texto={
                          formik.values.hand
                            ? t("general.yes")
                            : t("general.no")
                        }
                      />
                    </div>
                  </div>
                </div>
                <InputWithCornerHint
                  label={t("user.views.other")}
                  name="other"
                  value={formik.values.other}
                  onChange={formik.handleChange}
                />
              </div>
            )}
          </div>

          {/* Horas sentado */}
          <div className="flex items-center justify-between mt-5 gap-x-4">
            <label className="block text-sm font-medium text-gray-900">
              {t("user.views.sitting_hours")}
            </label>
            <div className="w-20">
              <InputWithCornerHint
                label=""
                type="number"
                name="sitting_hours"
                value={formik.values.sitting_hours}
                onChange={formik.handleChange}
              />
            </div>
          </div>

          {/* Actividad física */}
          <div className="mt-5">
            <div className="flex items-center justify-between gap-x-4">
              <label className="block text-sm font-medium text-gray-900">
                {t("user.views.physical_activity")}
              </label>
              <div className="w-40">
                <ToggleTextWhitLabel
                  checked={formik.values.activtyFisical}
                  onChange={() =>
                    formik.setFieldValue(
                      "activtyFisical",
                      !formik.values.activtyFisical
                    )
                  }
                  texto={
                    formik.values.activtyFisical
                      ? t("general.yes")
                      : t("general.no")
                  }
                />
              </div>
            </div>
            {formik.values.activtyFisical && (
              <div className="mt-4">
                <TextAreaHint
                  label=""
                  name="physical_activity_description"
                  value={formik.values.physical_activity_description}
                  onChange={formik.handleChange}
                />
              </div>
            )}
          </div>

          {/* Días de entrenamiento por semana */}
          <div className="flex items-center justify-between mt-5 gap-x-4">
            <label className="block text-sm font-medium text-gray-900">
              {t("user.views.days_week_train")}
            </label>
            <div className="w-20">
              <InputWithCornerHint
                label=""
                type="number"
                name="days_week_train"
                value={formik.values.days_week_train}
                onChange={formik.handleChange}
              />
            </div>
          </div>

          {/* Experiencia de entrenamiento */}
          <div className="mt-5">
            <TextAreaHint
              label={t("user.views.training_experience")}
              name="training_experience"
              value={formik.values.training_experience}
              onChange={formik.handleChange}
            />
          </div>

          {/* Entrenamiento con entrenador */}
          <div className="mt-5">
            <TextAreaHint
              label={t("user.views.training_with_trainer")}
              name="training_with_trainer"
              value={formik.values.training_with_trainer}
              onChange={formik.handleChange}
            />
          </div>

          {/* Descripción de entrenamiento semanal */}
          <div className="mt-5">
            <TextAreaHint
              label={t("user.views.describe_week_training")}
              name="describe_week_training"
              value={formik.values.describe_week_training}
              onChange={formik.handleChange}
            />
          </div>

          {/* Mejoras */}
          <div className="mt-5">
            <TextAreaHint
              label={t("user.views.improvements")}
              name="improvements"
              value={formik.values.improvements}
              onChange={formik.handleChange}
            />
          </div>

          {/* Gimnasio equipado */}
          <div className="mt-5">
            <div className="flex items-center justify-between gap-x-4">
              <label className="block text-sm font-medium text-gray-900">
                {t("user.views.well_equipped_gym")}
              </label>
              <div className="w-40">
                <ToggleTextWhitLabel
                  checked={formik.values.equipeGym}
                  onChange={() =>
                    formik.setFieldValue("equipeGym", !formik.values.equipeGym)
                  }
                  texto={
                    formik.values.equipeGym ? t("general.yes") : t("general.no")
                  }
                />
              </div>
            </div>
            {formik.values.equipeGym && (
              <div className="mt-4">
                <TextAreaHint
                  label=""
                  name="well_equipped_gym_description"
                  value={formik.values.well_equipped_gym_description}
                  onChange={formik.handleChange}
                />
              </div>
            )}
          </div>

          {/* Hora habitual de entrenamiento */}
          <div className="flex items-center justify-between mt-5 gap-x-4">
            <label className="block text-sm font-medium text-gray-900">
              {t("user.views.usual_training_time")}
            </label>
            <div className="w-40">
              <InputWithCornerHint
                label=""
                name="usual_training_time"
                value={formik.values.usual_training_time}
                onChange={formik.handleChange}
              />
            </div>
          </div>
        </div>

        <div>
          {/* Alimentos que no gustan */}
          <div className="mt-5">
            <TextAreaHint
              label={t("user.views.foods_not_like")}
              name="foods_not_like"
              value={formik.values.foods_not_like}
              onChange={formik.handleChange}
            />
          </div>

          {/* Intolerancias */}
          <div className="mt-5">
            <div className="flex items-center justify-between gap-x-4">
              <label className="block text-sm font-medium text-gray-900">
                {t("user.views.intolerance")}
              </label>
              <div className="w-40">
                <ToggleTextWhitLabel
                  checked={formik.values.intolerance}
                  onChange={() =>
                    formik.setFieldValue(
                      "intolerance",
                      !formik.values.intolerance
                    )
                  }
                  texto={
                    formik.values.intolerance
                      ? t("general.yes")
                      : t("general.no")
                  }
                />
              </div>
            </div>
            {formik.values.intolerance && (
              <div className="mt-4">
                <TextAreaHint
                  label=""
                  name="intolerance_description"
                  value={formik.values.intolerance_description}
                  onChange={formik.handleChange}
                />
              </div>
            )}
          </div>

          {/* Preferencias alimenticias */}
          <div className="mt-5">
            <TextAreaHint
              label={t("user.views.food_preferences")}
              name="food_preferences"
              value={formik.values.food_preferences}
              onChange={formik.handleChange}
            />
          </div>

          {/* Comidas al día */}
          <div className="mt-5">
            <TextAreaHint
              label={t("user.views.how_may_meal_day")}
              name="how_may_meal_day"
              value={formik.values.how_may_meal_day}
              onChange={formik.handleChange}
            />
          </div>

          {/* Comidas en casa y fuera */}
          <div className="mt-5">
            <TextAreaHint
              label={t("user.views.meals_in_home_and_outside")}
              name="meals_in_home_and_outside"
              value={formik.values.meals_in_home_and_outside}
              onChange={formik.handleChange}
            />
          </div>

          {/* Alimentos en casa y fuera */}
          <div className="mt-5">
            <TextAreaHint
              label={t("user.views.foods_in_home_and_outside")}
              name="foods_in_home_and_outside"
              value={formik.values.foods_in_home_and_outside}
              onChange={formik.handleChange}
            />
          </div>

          {/* Suplementación */}
          <div className="mt-5">
            <div className="flex items-center justify-between gap-x-4">
              <label className="block text-sm font-medium text-gray-900">
                {t("user.views.supplementation")}
              </label>
              <div className="w-40">
                <ToggleTextWhitLabel
                  checked={formik.values.supplementation}
                  onChange={() =>
                    formik.setFieldValue(
                      "supplementation",
                      !formik.values.supplementation
                    )
                  }
                  texto={
                    formik.values.supplementation
                      ? t("general.yes")
                      : t("general.no")
                  }
                />
              </div>
            </div>
            {formik.values.supplementation && (
              <div className="mt-4">
                <TextAreaHint
                  label=""
                  name="supplementation_description"
                  value={formik.values.supplementation_description}
                  onChange={formik.handleChange}
                />
              </div>
            )}
          </div>

          {/* Disposición a usar suplementación */}
          <div className="flex items-center justify-between mt-5 gap-x-4">
            <label className="block text-sm font-medium text-gray-900">
              {t("user.views.would_use_supplementation")}
            </label>
            <div className="w-40">
              <ToggleTextWhitLabel
                checked={formik.values.useSupplementation}
                onChange={() =>
                  formik.setFieldValue(
                    "useSupplementation",
                    !formik.values.useSupplementation
                  )
                }
                texto={
                  formik.values.useSupplementation
                    ? t("general.yes")
                    : t("general.no")
                }
              />
            </div>
          </div>

          {/* Botón para subir informes médicos */}
          <div className="flex items-center justify-between mt-5 gap-x-4">
            <h1 className="text-base text-gray-900">
              {t("user.views.medical_reports")}
            </h1>
            <ButtonSoft
              text={t("user.views.medical_reports_view_or_upload")}
              size="large"
              onClick={() => setShowUploadModal(true)}
            />
          </div>
        </div>
      </form>

      <DialogCreate
        tittle={t("user.views.medical_reports")}
        tittleButtonCancel={t("general.cancel")}
        tittleButtonCreate={t("general.create")}
        open={showUploadModal}
        setOpen={(open) => setShowUploadModal(open)}
        OnclickCancel={() => setShowUploadModal(false)}
      >
        <RenderFormUploadList
          accept="application/pdf"
          enpoint="/admin/medical-report/create"
          user_id={user_id}
          listLoader={isLoadingMedicalReports}
          refetchList={refetchMedicalReports}
          listImage={medicalReports?.medical_reports}
          deleteAction={({ image_id }) => {
            deleteMedicalReport({ id: user_id, item_id: image_id });
          }}
        />
      </DialogCreate>
    </>
  );
};

export default FormValuationStateHealth;
